// ReservationStepper.js
import React from 'react';
import PropTypes from 'prop-types';

// @mui
import {styled, alpha} from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import StepConnector from '@mui/material/StepConnector';
import useMediaQuery from '@mui/material/useMediaQuery';

// icons
import GroupRestaurantIcon from '@mui/icons-material/DinnerDining';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import AddTaskIcon from '@mui/icons-material/AddTask';

// utils
import {getContrastTextColor} from 'packages/common/utils/colorUtils';

// ----------------------------------------------------------------------

const StepperWrapper = styled(Box)(({theme}) => ({
  padding: theme.spacing(1, 2),
  backgroundColor: alpha(theme.palette.background.default, 0.6),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const CustomStepConnector = styled(StepConnector, {
  shouldForwardProp: (prop) => prop !== 'primaryColor',
})(({theme, primaryColor}) => ({
  '& .MuiStepConnector-line': {
    borderColor: primaryColor ?
      alpha(primaryColor, 0.2) :
      alpha(theme.palette.primary.main, 0.2),
  },
}));

const CustomStepIcon = styled('div', {
  shouldForwardProp: (prop) => !['primaryColor', 'active', 'completed'].includes(prop),
})(({theme, active, completed, primaryColor}) => {
  // Déterminer la couleur d'arrière-plan
  const backgroundColor = active ?
    (primaryColor || theme.palette.primary.main) :
    completed ?
      alpha(primaryColor || theme.palette.primary.main, 0.7) :
      alpha(theme.palette.grey[400], 0.3);

  // Déterminer la couleur du texte en fonction de la couleur d'arrière-plan
  const textColor = active || completed ?
    (primaryColor ? getContrastTextColor(primaryColor) : theme.palette.primary.contrastText) :
    theme.palette.text.secondary;

  return {
    'display': 'flex',
    'alignItems': 'center',
    'justifyContent': 'center',
    'width': 32,
    'height': 32,
    'borderRadius': '50%',
    'backgroundColor': backgroundColor,
    'color': textColor,
    '& svg': {
      fontSize: 16,
    },
  };
});

// ----------------------------------------------------------------------

export function ReservationStepper({activeStep, primaryColor}) {
  const steps = [
    {label: 'Réservation', icon: <GroupRestaurantIcon fontSize="small" />},
    {label: 'Date', icon: <CalendarMonthIcon fontSize="small" />},
    {label: 'Horaire', icon: <AccessTimeIcon fontSize="small" />},
    {label: 'Infos', icon: <PersonIcon fontSize="small" />},
    {label: 'Détails', icon: <AddTaskIcon fontSize="small" />},
  ];

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  return (
    <StepperWrapper>
      <Stepper
        activeStep={activeStep}
        alternativeLabel={!isMobile}
        connector={<CustomStepConnector primaryColor={primaryColor} />}
        sx={{
          '& .MuiStepLabel-root': {padding: isMobile ? '0 4px' : '0 8px'},
          '& .MuiStepLabel-label.MuiStepLabel-alternativeLabel': {marginTop: 0.5},
          'py': 1,
        }}
      >
        {steps.map((step, index) => (
          <Step key={step.label} completed={index < activeStep}>
            <StepLabel
              StepIconComponent={(props) => (
                <CustomStepIcon
                  active={props.active}
                  completed={props.completed}
                  primaryColor={primaryColor}
                >
                  {step.icon}
                </CustomStepIcon>
              )}
            >
              {!isMobile && (
                <Box
                  component="span"
                  sx={{
                    fontSize: '0.75rem',
                    color: index < activeStep ? (primaryColor || 'inherit') : 'inherit',
                  }}
                >
                  {step.label}
                </Box>
              )}
            </StepLabel>
          </Step>
        ))}
      </Stepper>
    </StepperWrapper>
  );
}

ReservationStepper.propTypes = {
  activeStep: PropTypes.number.isRequired,
  primaryColor: PropTypes.string,
};
